// Kort sammanfattning av en tjänstekatalog, för korten i Kom igång
// (designen Admin-Start): antal tjänster och vad den första tjänsten kostar.
import type { components } from '../api/schema.js';

export type ServiceCatalog = components['schemas']['ServiceCatalog'];
type Service = ServiceCatalog['services'][number];

const kronor = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });

/** 150000 öre blir "1 500 kr". */
export function kr(ore: number): string {
  return `${kronor.format(Math.round(ore / 100))} kr`;
}

const PERIOD = { monthly: '/mån', yearly: '/år', once: '' } as const;

export function antalTjanster(n: number): string {
  return n === 1 ? '1 tjänst' : `${n} tjänster`;
}

/** Texten före beloppet och beloppet, som visas med siffertypsnittet. */
export interface Prisrad {
  text: string;
  belopp: string;
}

function prisFor(tjanst: Service): Prisrad {
  const period = PERIOD[tjanst.billing];
  const p = tjanst.pricing;
  switch (p.type) {
    case 'fixed':
      return { text: tjanst.label, belopp: `${kr(p.price)}${period}` };
    case 'perUnit':
      return { text: tjanst.label, belopp: `${kr(p.price)}${period} per ${p.unit}` };
    case 'tiered':
      return {
        text: `${tjanst.label} från`,
        belopp: `${kr(Math.min(...p.tiers.map((t) => t.price)))}${period}`,
      };
    case 'byLegalForm':
      return {
        text: `${tjanst.label} från`,
        belopp: `${kr(Math.min(...Object.values(p.prices).filter((v) => v !== undefined)))}${period}`,
      };
    case 'hourly':
      return { text: 'Timpris', belopp: `${kr(p.rate)}/tim` };
  }
}

/** Priset för katalogens första löpande tjänst, eller den första tjänsten. */
export function forstaPris(katalog: ServiceCatalog): Prisrad | null {
  const tjanst = katalog.services.find((s) => s.billing === 'monthly') ?? katalog.services[0];
  return tjanst ? prisFor(tjanst) : null;
}

/**
 * En tom katalog att börja från. Inställningarna är samma som i startmallarna,
 * och byrån ändrar dem i tjänstekatalogen (steg 8.3).
 */
export const TOM_KATALOG: ServiceCatalog = {
  currency: 'SEK',
  vatRate: 0.25,
  services: [],
  aiAdjustmentPercent: { min: -10, max: 15 },
  maxAutoQuoteMonthly: 900000,
  reviewFlags: [],
  roundTo: 500,
  validDays: 30,
  terms: [],
};
